"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import {
  deleteDocumentCookie,
  setDocumentCookie,
} from "@packages/lib/utlils/cookie-utils";

const COOKIE_NAME = "urlHistory";
const IGNORED_PATHS = ["/authverification"];

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  setDocumentCookie({
    name,
    value: `${encodeURIComponent(value)}`
  });
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  deleteDocumentCookie({ name });
}

export default function useTrackUrlHistory() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (IGNORED_PATHS.some((path) => pathname.startsWith(path))) return;
    const fullUrl =
      pathname + (searchParams?.toString() ? `?${searchParams}` : "");
    const cookieRaw = getCookie(COOKIE_NAME);
    let history: { previous?: string; current?: string } = {};

    try {
      if (cookieRaw) {
        history = JSON.parse(cookieRaw);
      }
    } catch {
      history = {};
    }

    if (history.current !== fullUrl) {
      const updated = {
        previous: history.current || "",
        current: fullUrl,
      };
      setCookie(COOKIE_NAME, JSON.stringify(updated));
    }
    const handlePageHide = (event: PageTransitionEvent) => {
      // Only clear when the page is actually being discarded (real navigation away).
      // When event.persisted is true (bfcache) or a new tab is opened via window.open,
      // the current page remains — keep the cookie so referral tracking (getReferrer)
      // still works for flows like the exit-intent popup.
      if (!event.persisted) {
        deleteCookie(COOKIE_NAME);
      }
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [pathname, searchParams]);
}
